import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { withBase } from '../config/api'
import { primaryButtonStyle } from '../styles/buttonStyles'
import {
  footerModalActionsStyle,
  footerModalBoxStyle,
  footerModalOverlayStyle
} from '../styles/footerModalStyles'

const COMPANY_FIELDS = [
  ['name', 'COMPANY_NAME'],
  ['address', 'ADDRESS'],
  ['city', 'CITY'],
  ['zip', 'ZIP'],
  ['country', 'COUNTRY'],
  ['ico', 'ICO'],
  ['dic', 'DIC'],
  ['ic_dph', 'IC_DPH'],
  ['registration', 'REGISTRATION'],
  ['support_email', 'SUPPORT_EMAIL'],
  ['info_email', 'INFO_EMAIL'],
  ['phone', 'PHONE'],
  ['bank_name', 'BANK_NAME'],
  ['iban', 'IBAN'],
  ['swift', 'SWIFT']
]

const AboutUsModal = ({ open, onClose }) => {
  const { t } = useTranslation()

  const [hoverBtn, setHoverBtn] = useState(null)
  const [pressedBtn, setPressedBtn] = useState(null)
  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return

    const controller = new AbortController()

    const loadCompany = async () => {
      setLoading(true)
      setError('')

      try {
        const res = await fetch(withBase('/company/about'), {
          signal: controller.signal
        })

        if (!res.ok) {
          throw new Error('company-load-failed')
        }

        const data = await res.json()
        setCompany(data)
      } catch (err) {
        if (err.name !== 'AbortError') {
          setCompany(null)
          setError(t('COMPANY_LOAD_ERROR'))
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }

    loadCompany()

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      controller.abort()
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onClose, t])

  if (!open) return null

  const visibleFields = COMPANY_FIELDS.filter(([key]) => {
    const value = company?.[key]

    return (
      value !== null &&
      value !== undefined &&
      String(value).trim() !== ''
    )
  })

  const getCompanyFieldValue = (key) => {
    const value = company?.[key]

    if (key === 'country') {
      return t(value, {
        defaultValue: value
      })
    }

    return value
  }

  return (
    <>
      <div
        onClick={(e) => {
          e.stopPropagation()
          onClose()
        }}
        style={footerModalOverlayStyle()}
      />

      <div
        onClick={(e) => e.stopPropagation()}
        style={footerModalBoxStyle({
          width: 620,
          maxWidth: 'calc(100vw - 32px)'
        })}
      >
        <div
          style={{
            marginBottom: 14,
            fontWeight: 'bold',
            fontSize: 24
          }}
        >
          {t('ABOUT_US')}
        </div>

        <div
          style={{
            marginBottom: 18,
            fontSize: 14,
            lineHeight: 1.55
          }}
        >
          <p style={{ margin: '0 0 12px 0' }}>
            {t('ABOUT_US_PROJECT_TEXT')}
          </p>

          <div
            style={{
              marginBottom: 8,
              fontWeight: 'bold',
              fontSize: 16
            }}
          >
            {t('ABOUT_US_ESTONIA_TITLE')}
          </div>

          <p style={{ margin: '0 0 12px 0' }}>
            {t('ABOUT_US_ESTONIA_TEXT1')}
          </p>
        </div>

        <div
          style={{
            marginBottom: 10,
            fontWeight: 'bold',
            fontSize: 16
          }}
        >
          {t('ABOUT_US_OPERATOR_TITLE')}
        </div>

        {loading && (
          <div
            style={{
              whiteSpace: 'pre-line',
              fontSize: 16
            }}
          >
            {t('LOADING')}
          </div>
        )}

        {!loading && error && (
          <div
            style={{
              whiteSpace: 'pre-line',
              fontSize: 16
            }}
          >
            {error}
          </div>
        )}

        {!loading && !error && (
          <div style={{ fontSize: 12 }}>
            {visibleFields.map(([key, labelKey]) => (
              <div key={key}>
                {key === 'bank_name' && (
                  <div
                    style={{
                      marginTop: 14,
                      marginBottom: 6,
                      fontWeight: 'bold',
                      fontSize: 16
                    }}
                  >
                    {t('BANK_CONTACT')}
                  </div>
                )}

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '180px 1fr',
                    gap: 12,
                    padding: '3px 0',
                    borderBottom:
                      '1px solid rgba(217,255,228,0.12)'
                  }}
                >
                  <strong>{t(labelKey)}</strong>

                  <span
                    style={{
                      minWidth: 0,
                      overflowWrap: 'anywhere'
                    }}
                  >
                    {getCompanyFieldValue(key)}

                    {key === 'registration' && (
                      <>
                        {` ${t('ABOUT_US_REGISTERED')}`}

                        <div style={{ marginTop: 3 }}>
                          <a
                            href="https://ariregister.rik.ee/eng/company/17553401/EnerqiQ-Net-O%C3%9C"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#66d9c2',
                              textDecoration: 'underline'
                            }}
                          >
                            {t('ABOUT_US_REGISTER_LINK')}
                          </a>
                        </div>
                      </>
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={footerModalActionsStyle}>
          <button
            onClick={onClose}
            style={primaryButtonStyle(
              pressedBtn,
              'about-us-close',
              hoverBtn === 'about-us-close'
            )}
            onMouseEnter={() => setHoverBtn('about-us-close')}
            onMouseLeave={() => {
              setHoverBtn(null)
              setPressedBtn(null)
            }}
            onMouseDown={() => setPressedBtn('about-us-close')}
            onMouseUp={() => setPressedBtn(null)}
          >
            {t('CLOSE')}
          </button>
        </div>
      </div>
    </>
  )
}

export default AboutUsModal